import Vue from 'vue';
import Router from 'vue-router';
import * as AmplifyModules from 'aws-amplify';
import {AmplifyPlugin, AmplifyEventBus} from 'aws-amplify-vue';
import store from '../store/store';
import {Auth} from '@/Auth'
import {BrandsList, BrandDetails, BrandEdit, BrandAdd} from '@/Brands';
import {StoresList, StoreDetails, StoreEdit, StoreAdd, StoreDashboard} from '@/Stores';
import {ReservationsList, ReservationDetails, ReservationAdd} from '@/Reservations';
import {
  Help,
  Scan,
  Search,
  SwitchPage,
  Favorites,
  Profile,
  ChangePassword,
  Error403Page,
  Error404Page,
  Error500Page,
  SigningPage,
  PrivacyPage
} from '@/Pages'
import {PAGE_IDS} from "@/utils/constants"


Vue.use(Router);
Vue.use(AmplifyPlugin, AmplifyModules);


AmplifyEventBus.$on('authState', async (state) => {
  if (state === 'signedOut') {
    store.dispatch('reinitialize', null)
    store.dispatch('initialize').then(() => {
      router.push(store.getters.getDefaultPage)
    })
  } else if (state === 'signedIn') {
    store.dispatch('reinitialize')
    store.dispatch('initialize').then(() => {
      router.push(store.getters.getDefaultPage)
    })
  }
});


const router = new Router({
    mode: 'history',
    scrollBehavior: (to, from, savedPosition) => {
      return {x: 0, y: 0}
    },
    routes: [
      {
        path: '/search',
        name: PAGE_IDS.SEARCH,
        component: Search,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Select Store",
          allowed: (store, to) => store.getters.isVisitor,
        }
      },
      {
        path: '/scan',
        name: PAGE_IDS.SCAN,
        component: Scan,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Scan QR Code",
          allowed: (store, to) => store.getters.isSecurity || (store.getters.getCurrentStore && store.getters.isStoreManagerOfStore(store.getters.getCurrentStore))
        }
      },
      {
        path: '/favorites',
        name: PAGE_IDS.FAVORITES,
        component: Favorites,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Favorites",
          allowed: (store, to) => store.getters.isVisitor
        }
      },
      {
        path: '/brands',
        name: PAGE_IDS.BRANDS_LIST,
        component: BrandsList,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Brands List",
          allowed: (store, to) => store.getters.isSuperAdmin
        }
      },
      {
        path: '/brand/dashboard/all',
        name: PAGE_IDS.BRAND_DASHBOARD,
        component: StoreDashboard,
        meta: {
          mode:'brand',
          drawer_needed: true,
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Brand Dashboard",
          allowed: (store, to) => store.getters.isBrandManager
        }
      },
      {
        path: '/brand/dashboard/:store_id',
        name: PAGE_IDS.BRAND_DASHBOARD_SINGLE_STORE,
        component: StoreDashboard,
        meta: {
          mode:'store_in_brand',
          drawer_needed: true,
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Brand Dashboard Store Details",
          parent: (route, store) => {
            return {'name': PAGE_IDS.BRAND_DASHBOARD}
          },
          allowed: (store, to) => store.getters.isBrandManager
        }
      },
      {
        path: '/brands/:brand_id',
        name: PAGE_IDS.BRAND_DETAILS,
        component: BrandDetails,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Brand Details",
          parent: (route, store) => {
            return {'name': PAGE_IDS.BRANDS_LIST}
          },
          allowed: (store, to) => (store.getters.isSuperAdmin) || (store.getters.isBrandManagerOfBrand(to.params.brand_id))
        }
      },
      {
        path: '/brands/:brand_id/edit',
        name: PAGE_IDS.BRAND_EDIT,
        component: BrandEdit,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Edit Brand",
          parent: (route, store) => {
            return {'name': PAGE_IDS.BRANDS_LIST}
          },
          allowed: (store, to) => (store.getters.isSuperAdmin) || (store.getters.isBrandManagerOfBrand(to.params.brand_id))
        }
      },
      {
        path: '/brands/add',
        name: PAGE_IDS.BRAND_ADD,
        component: BrandAdd,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Add Brand",
          parent: (route, store) => {
            return {'name': PAGE_IDS.BRANDS_LIST}
          },
          allowed: (store, to) => store.getters.isSuperAdmin

        }
      },
      {
        path: '/stores',
        name: PAGE_IDS.STORES_LIST,
        component: StoresList,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Stores List",
          allowed: (store, to) => (store.getters.isStoreManager) || (store.getters.isBrandManager)
        }
      },
      {
        path: '/stores/:store_id',
        name: PAGE_IDS.STORE_DETAILS,
        component: StoreDetails,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Store Details",
          parent: (route, store) => store.getters.getCurrentStore ? undefined : {'name': PAGE_IDS.STORES_LIST},
          allowed: (store, to) => (store.getters.isStoreManagerOfStore(to.params.store_id)) || (store.getters.isBrandManagerOfStore(to.params.store_id))
        }
      },
      {
        path: '/stores/dashboard/:store_id',
        name: PAGE_IDS.STORE_DASHBOARD,
        component: StoreDashboard,
        meta: {
          mode:'store',
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Store Dashboard",
          parent: (route, store) => store.getters.getCurrentStore ? undefined : {'name': PAGE_IDS.SEARCH},
          allowed: (store, to) => (store.getters.isStoreManagerOfStore(to.params.store_id)) || (store.getters.isBrandManagerOfStore(to.params.store_id))
        }
      },
      {
        path: '/stores/:store_id/edit',
        name: PAGE_IDS.STORE_EDIT,
        component: StoreEdit,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Edit Store",
          parent: (route, store) => store.getters.getCurrentStore ? store.getters.getDefaultPage : {'name': PAGE_IDS.STORES_LIST},
          allowed: (store, to) => (store.getters.isStoreManagerOfStore(to.params.store_id)) || (store.getters.isBrandManagerOfStore(to.params.store_id))
        }
      },
      {
        path: '/stores/add',
        name: PAGE_IDS.STORE_ADD,
        component: StoreAdd,
        meta: {

          requiresAuth: true,
          page_title_needed: true,
          page_title: "Add Store",
          parent: (route, store) => {
            return {'name': PAGE_IDS.STORES_LIST}
          },
          allowed: (store, to) => store.getters.isBrandManager
        }
      },
      {
        path: '/reservations',
        name: PAGE_IDS.RESERVATIONS,
        component: ReservationsList,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "My Spots",
          allowed: (store, to) => store.getters.isVisitor
        }
      },
      {
        path: '/reservations/:reservation_id',
        name: PAGE_IDS.RESERVATION_DETAIL,
        component: ReservationDetails,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Reservation Details",
          parent: (route, store) => {
            return {'name': PAGE_IDS.RESERVATIONS}
          },
          allowed: (store, to) => store.getters.isVisitor && store.getters.isReservationOwner(to.params.reservation_id)
        }
      },
      {
        path: '/add_reservation/:store_id',
        name: PAGE_IDS.RESERVATION_ADD,
        component: ReservationAdd,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Reserve a Spot",
          parent: (route, store) => {
            return {'name': PAGE_IDS.SEARCH}
          },
          allowed: (store, to) => store.getters.isVisitor
        }
      },
      {
        path: '/profile',
        name: PAGE_IDS.PROFILE,
        component: Profile,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Your Profile",
        }
      },
      {
        path: '/change_password',
        name: PAGE_IDS.CHANGE_PASSWORD,
        component: ChangePassword,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Change Password",
          parent: (route, store) => {
            return {'name': PAGE_IDS.PROFILE}
          },
        }
      },
      {
        path: '/help',
        name: PAGE_IDS.HOW_IT_WORKS,
        component: Help,
        meta: {
          page_title_needed: true,
          page_title: "How it Works",
          parent: (route, store) => {
            return store.getters.userIsAuthenticated ? (route.query.first_time ? {'name': PAGE_IDS.SEARCH} : {'name': PAGE_IDS.PROFILE}) : {'name': PAGE_IDS.AUTH}
          }
        }
      },
      {
        path: '/privacy_page',
        name: PAGE_IDS.PRIVACY_PAGE,
        component: PrivacyPage,
        meta: {
          page_title_needed: true,
          page_title: "Privacy",
          parent: (route, store) => {
            return store.getters.userIsAuthenticated ? (route.query.first_time ? {'name': PAGE_IDS.SEARCH} : {'name': PAGE_IDS.PROFILE}) : {'name': PAGE_IDS.AUTH}
          }
        }
      },
      {
        path: '/switch',
        name: PAGE_IDS.SWITCH,
        component: SwitchPage,
        meta: {
          requiresAuth: true,
          page_title_needed: true,
          page_title: "Switch",
          allowed: (store, to) => store.getters.switchAvailable
        }
      },
      {
        path: '/auth',
        name: PAGE_IDS.AUTH,
        component: Auth,
        meta: {
          page_title_needed: false,
          page_title: 'Authentication',
          allowed: (store, to) => !store.getters.userIsAuthenticated
        }
      },
      {
        path: '/signin',
        name: PAGE_IDS.SIGNOUT,
        component: SigningPage,
        meta: {
          page_title_needed: true,
          page_title: "Signing In",
        }
      },
      {
        path: '/signout',
        name: PAGE_IDS.SIGNOUT,
        component: SigningPage,
        meta: {
          page_title_needed: true,
          page_title: "Signing Out",
        }
      },
      {
        path: '/500',
        name: PAGE_IDS.PAGE_500,
        component: Error500Page,
        meta: {
          page_title_needed: true,
          page_title: "Server Error",
        }
      },
      {
        path: '/404',
        name: PAGE_IDS.PAGE_404,
        component: Error404Page,
        meta: {
          page_title_needed: true,
          page_title: "Not Found",
        }
      },
      {
        path: '/403',
        name: PAGE_IDS.PAGE_403,
        component: Error403Page,
        meta: {
          page_title_needed: true,
          page_title: "No Permissions",
        }
      },
      {
        path: '/',
        redirect: to => store.getters.getDefaultPage
      },
      {
        path: '*',
        redirect: {name: PAGE_IDS.PAGE_404}
      }
    ]
  })
;

router.beforeResolve(async (to, from, next) => {
  if (store.getters.addUserModalShown) {
    store.dispatch('hideAddUserModal')
  }
  if (store.getters.isInitialized == false) {
    store.dispatch('initialize').then(() => {
      if ((to.name != PAGE_IDS.SIGNIN) && (to.name != PAGE_IDS.SIGNOUT) && (to.name != PAGE_IDS.AUTH)) {
        if (to.matched) {
          return next({'name':to.name,'meta':to.meta,'params':to.params})
        } 
      } else {
        return next()
      }
    }).catch(
      (e) => {
        next({'name': PAGE_IDS.PAGE_500})
      }
    )
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!store.getters.currentUser) {
      return next({
        name: PAGE_IDS.AUTH,
        query: {
          redirect: to.fullPath,
        }
      });
    }
    if (to.meta.allowed) {
      if (!to.meta.allowed(store, to)) {
        next(store.getters.getDefaultPage)
      }
    }
  }
  return next()
})

export default router



// WEBPACK FOOTER //
// ./src/router/index.js